import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';

const fmtDate = (d) => {
  if (!d) return '—';
  const s = (typeof d === 'string' ? d : '').slice(0, 10);
  const [y, m, day] = s.split('-');
  return day && m && y ? day + '/' + m + '/' + y : '—';
};
const fmtEur = v => Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC';

const STATUS_BADGE = {
  bozza:    { backgroundColor: '#F5F5F5', color: '#555', label: 'Bozza' },
  inviata:  { backgroundColor: '#EEF3FF', color: '#2D4BA0', label: 'Inviata' },
  pagata:   { backgroundColor: '#E8F5EF', color: '#0F6E56', label: 'Saldata' },
  scaduta:  { backgroundColor: '#FDEEEE', color: '#9B1C1C', label: 'Scaduta' },
  annullata:{ backgroundColor: '#F5F5F5', color: '#555', label: 'Annullata' },
};

const s = StyleSheet.create({
  page: { paddingTop: 71, paddingBottom: 71, paddingLeft: 40, paddingRight: 40, fontSize: 8, fontFamily: 'Helvetica', color: '#1a1a2e' },
  headerBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 5, backgroundColor: '#2EA084' },
  headerWrap: { position: 'absolute', top: 12, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', paddingBottom: 8, borderBottom: '1pt solid #e9ecef' },
  headerLeft: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#2EA084' },
  headerRight: { fontSize: 8, color: '#868e96', textAlign: 'right' },
  footer: { position: 'absolute', bottom: 24, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', fontSize: 7, color: '#868e96', borderTop: '0.5pt solid #e9ecef', paddingTop: 6 },
  sh: { borderLeft: '3pt solid #2EA084', paddingLeft: 8, paddingTop: 4, paddingBottom: 4, fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#0A1628', backgroundColor: '#fff', marginBottom: 6, marginTop: 14 },
  kpiRow: { flexDirection: 'row', marginBottom: 8 },
  kpiBox: { border: '1pt solid #e9ecef', borderRadius: 4, padding: '8 10', flex: 1, margin: '0 4' },
  kpiL: { fontSize: 7, color: '#868e96', textTransform: 'uppercase', marginBottom: 2 },
  kpiV: { fontSize: 12, fontFamily: 'Helvetica-Bold' },
  th: { flexDirection: 'row', backgroundColor: '#f8f9fa', borderBottom: '1pt solid #e9ecef', padding: '4 6', fontFamily: 'Helvetica-Bold', fontSize: 7 },
  tr: { flexDirection: 'row', borderBottom: '0.5pt solid #e9ecef', padding: '4 6', fontSize: 8 },
  trA: { flexDirection: 'row', borderBottom: '0.5pt solid #e9ecef', padding: '4 6', fontSize: 8, backgroundColor: '#f8f9fa' },
  trT: { flexDirection: 'row', padding: '5 6', fontSize: 8, fontFamily: 'Helvetica-Bold', backgroundColor: '#F5F5F5', color: '#0A1628', borderTop: '1pt solid #2EA084' },
  badge: { fontSize: 7, padding: '1 4', borderRadius: 2 },
});

export default function FattureTabPDF({ fatture, milestone, cantiere, azienda, budget }) {
  const oggi = new Date().toLocaleDateString('it-IT');
  const fatt = fatture || [];
  const miles = milestone || [];

  const valoreContratto = budget?.valore_commessa || 0;
  // Fatturato = imponibile delle fatture emesse (escluse bozze e annullate),
  // coerente con FattureTab e il principio piattaforma (economia su imponibile).
  const fatturato = fatt.reduce((s, f) => s + ((f.status !== 'annullata' && f.status !== 'bozza') ? Number(f.imponibile || 0) : 0), 0);
  const residuo = valoreContratto - fatturato;

  return (
    <Document>
      <Page size="A4" style={s.page}>
        <View style={s.headerBar} fixed />
        <View style={s.headerWrap} fixed>
          <Text style={s.headerLeft}>Fatture & SAL — {cantiere?.titolo || ''}</Text>
          <View style={s.headerRight}>
            <Text>{azienda?.nome || ''}</Text>
            <Text>Generato il {oggi}</Text>
          </View>
        </View>

        {/* KPI */}
        <Text style={s.sh}>Riepilogo</Text>
        <View style={s.kpiRow}>
          <View style={s.kpiBox}>
            <Text style={s.kpiL}>Valore contratto</Text>
            <Text style={s.kpiV}>{fmtEur(valoreContratto)}</Text>
          </View>
          <View style={s.kpiBox}>
            <Text style={s.kpiL}>Fatturato</Text>
            <Text style={[s.kpiV, { color: '#2EA084' }]}>{fmtEur(fatturato)}</Text>
          </View>
          <View style={s.kpiBox}>
            <Text style={s.kpiL}>Residuo</Text>
            <Text style={[s.kpiV, { color: residuo >= 0 ? '#868e96' : '#E85D5D' }]}>{fmtEur(residuo)}</Text>
          </View>
        </View>

        {/* Fatture */}
        <Text style={s.sh}>Fatture ({fatt.length})</Text>
        {fatt.length === 0 ? (
          <Text style={{ fontSize: 8, color: '#868e96', padding: '6 0' }}>Nessuna fattura emessa.</Text>
        ) : (
          <>
            <View style={s.th}>
              <Text style={{ flex: 2 }}>Numero</Text>
              <Text style={{ flex: 1.5 }}>Stato</Text>
              <Text style={{ flex: 1.5 }}>Scadenza</Text>
              <Text style={{ flex: 1.5, textAlign: 'right' }}>Imponibile</Text>
            </View>
            {fatt.map((f, i) => {
              const badge = STATUS_BADGE[f.status] || STATUS_BADGE.bozza;
              return (
                <View key={f.id || i} style={i % 2 === 0 ? s.tr : s.trA}>
                  <Text style={{ flex: 2 }}>{f.numero || '—'}</Text>
                  <View style={{ flex: 1.5 }}>
                    <Text style={[s.badge, { backgroundColor: badge.backgroundColor, color: badge.color, alignSelf: 'flex-start' }]}>
                      {badge.label}
                    </Text>
                  </View>
                  <Text style={{ flex: 1.5 }}>{fmtDate(f.data_scadenza)}</Text>
                  <Text style={{ flex: 1.5, textAlign: 'right' }}>{fmtEur(f.imponibile)}</Text>
                </View>
              );
            })}
            <View style={s.trT}>
              <Text style={{ flex: 2 }}>Totale</Text>
              <Text style={{ flex: 1.5 }} /><Text style={{ flex: 1.5 }} />
              <Text style={{ flex: 1.5, textAlign: 'right' }}>{fmtEur(fatturato)}</Text>
            </View>
          </>
        )}

        {/* SAL finale suggerito */}
        {residuo > 0 && fatt.length > 0 && (
          <View style={{ padding: '8 10', marginTop: 8, border: '1pt dashed #e9ecef', borderRadius: 4 }}>
            <Text style={{ fontSize: 8, color: '#868e96' }}>SAL finale — {fmtEur(residuo)} · da emettere a fine lavori</Text>
          </View>
        )}

        {/* Milestone */}
        {miles.length > 0 && (
          <>
            <Text style={s.sh}>SAL / Milestone ({miles.length})</Text>
            <View style={s.th}>
              <Text style={{ flex: 3 }}>Nome</Text>
              <Text style={{ flex: 1.5 }}>Data</Text>
              <Text style={{ flex: 1.5, textAlign: 'right' }}>Importo SAL</Text>
              <Text style={{ flex: 1, textAlign: 'center' }}>Raggiunta</Text>
            </View>
            {miles.map((m, i) => (
              <View key={m.id || i} style={i % 2 === 0 ? s.tr : s.trA}>
                <Text style={{ flex: 3 }}>{m.nome}</Text>
                <Text style={{ flex: 1.5 }}>{fmtDate(m.data)}</Text>
                <Text style={{ flex: 1.5, textAlign: 'right' }}>{m.importo_sal ? fmtEur(m.importo_sal) : '—'}</Text>
                <Text style={{ flex: 1, textAlign: 'center', color: m.raggiunta ? '#2EA084' : '#868e96' }}>
                  {m.raggiunta ? 'Si' : 'No'}
                </Text>
              </View>
            ))}
          </>
        )}

        <View style={s.footer} fixed>
          <Text>mAIstro — Fatture & SAL</Text>
          <Text>{cantiere?.titolo || ''}</Text>
          <Text render={({ pageNumber, totalPages }) => 'Pagina ' + pageNumber + ' di ' + totalPages} />
        </View>
      </Page>
    </Document>
  );
}
